import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Table, 
  Button, 
  Modal, 
  Form, 
  Alert, 
  Badge, 
  Spinner 
} from 'react-bootstrap';
import axios from 'axios';

const Integrations = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [generatedToken, setGeneratedToken] = useState('');
  const [newClient, setNewClient] = useState({
    name: '',
    client_id: '',
    client_secret: ''
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/oauth2/clients');
      setClients(response.data);
      setError('');
    } catch (err) {
      setError('Failed to fetch OAuth2 clients: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClient = async () => {
    try {
      const response = await axios.post('/api/oauth2/clients', newClient);
      setClients([...clients, response.data]);
      setShowCreateModal(false);
      setNewClient({ name: '', client_id: '', client_secret: '' });
      setSuccess('OAuth2 client created successfully!');
      setError('');
    } catch (err) {
      setError('Failed to create OAuth2 client: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDeleteClient = async () => {
    if (!selectedClient) return;
    
    try {
      await axios.delete(`/api/oauth2/clients/${selectedClient.client_id}`);
      setClients(clients.filter(client => client.client_id !== selectedClient.client_id));
      setShowDeleteModal(false);
      setSelectedClient(null);
      setSuccess('OAuth2 client deleted successfully!');
      setError('');
    } catch (err) {
      setError('Failed to delete OAuth2 client: ' + (err.response?.data?.detail || err.message));
    }
  };

  const generateToken = async (clientId, clientSecret) => {
    try {
      const response = await axios.post('/api/oauth2/token', {
        client_id: clientId,
        client_secret: clientSecret,
        grant_type: 'client_credentials',
        scope: 'webhook'
      });
      setGeneratedToken(response.data.access_token);
      setSuccess('Access token generated successfully!');
    } catch (err) {
      setError('Failed to generate token: ' + (err.response?.data?.detail || err.message));
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSuccess('Copied to clipboard!');
  };

  const generateRandomCredentials = () => {
    const randomId = 'client_' + Math.random().toString(36).substr(2, 9);
    const randomSecret = 'secret_' + Math.random().toString(36).substr(2, 15);
    setNewClient({
      ...newClient,
      client_id: randomId,
      client_secret: randomSecret
    });
  };

  if (loading) {
    return (
      <Container className="mt-4">
        <div className="d-flex justify-content-center">
          <Spinner animation="border" variant="warning" />
        </div>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>🔗 Integrations</h2>
            <Button 
              variant="primary" 
              onClick={() => setShowCreateModal(true)}
            >
              Create New OAuth2 Client
            </Button>
          </div>

          {error && (
            <Alert variant="danger" onClose={() => setError('')} dismissible>
              {error}
            </Alert>
          )}

          {success && (
            <Alert variant="success" onClose={() => setSuccess('')} dismissible>
              {success}
            </Alert>
          )}

          {generatedToken && (
            <Alert variant="info" onClose={() => setGeneratedToken('')} dismissible>
              <h6>🎫 Generated Access Token:</h6>
              <code className="bg-light px-2 py-1 rounded">{generatedToken}</code>
              <Button 
                variant="outline-primary" 
                size="sm" 
                className="ms-2"
                onClick={() => copyToClipboard(generatedToken)}
              >
                📋 Copy
              </Button>
            </Alert>
          )}

          {/* Integration URLs */}
          <Card className="mb-4 shadow-sm">
            <Card.Header className="bg-info text-white">
              <h5 className="mb-0">🌐 Integration URLs</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <h6>🔧 OAuth2 Token URL:</h6>
                  <div className="d-flex gap-2 align-items-center mb-3">
                    <code className="bg-light px-2 py-1 rounded flex-grow-1">
                      https://lms.iconinc.co.uk/api/oauth2/token
                    </code>
                    <Button 
                      variant="outline-secondary" 
                      size="sm"
                      onClick={() => copyToClipboard('https://lms.iconinc.co.uk/api/oauth2/token')}
                    >
                      📋 Copy
                    </Button>
                  </div>
                  
                  <h6>📡 VAPI Webhook URL:</h6>
                  <div className="d-flex gap-2 align-items-center mb-3">
                    <code className="bg-light px-2 py-1 rounded flex-grow-1">
                      https://lms.iconinc.co.uk/api/vapi/webhook
                    </code>
                    <Button 
                      variant="outline-secondary" 
                      size="sm"
                      onClick={() => copyToClipboard('https://lms.iconinc.co.uk/api/vapi/webhook')}
                    >
                      📋 Copy
                    </Button>
                  </div>
                </Col>
                
                <Col md={6}>
                  <h6>📡 Trengo Webhook URL:</h6>
                  <div className="d-flex gap-2 align-items-center mb-3">
                    <code className="bg-light px-2 py-1 rounded flex-grow-1">
                      https://lms.iconinc.co.uk/api/trengo/webhook
                    </code>
                    <Button 
                      variant="outline-secondary" 
                      size="sm"
                      onClick={() => copyToClipboard('https://lms.iconinc.co.uk/api/trengo/webhook')}
                    >
                      📋 Copy
                    </Button>
                  </div>
                  
                  <h6>🔑 API Base URL:</h6>
                  <div className="d-flex gap-2 align-items-center">
                    <code className="bg-light px-2 py-1 rounded flex-grow-1">
                      https://lms.iconinc.co.uk/api
                    </code>
                    <Button 
                      variant="outline-secondary" 
                      size="sm"
                      onClick={() => copyToClipboard('https://lms.iconinc.co.uk/api')}
                    >
                      📋 Copy
                    </Button>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* OAuth2 Clients */}
          <Card className="shadow-sm">
            <Card.Header className="bg-primary text-white">
              <h5 className="mb-0">🔑 OAuth2 Clients</h5>
            </Card.Header>
            <Card.Body>
              {clients.length === 0 ? (
                <div className="text-center py-5">
                  <div className="mb-3">
                    <span style={{ fontSize: '3rem' }}>🔑</span>
                  </div>
                  <h5>No OAuth2 clients found</h5>
                  <p className="text-muted">Create your first client to get started with OAuth2 authentication.</p>
                  <Button 
                    variant="primary" 
                    onClick={() => setShowCreateModal(true)}
                  >
                    Create Your First Client
                  </Button>
                </div>
              ) : (
                <Table responsive striped hover>
                  <thead className="table-dark">
                    <tr>
                      <th>Client ID</th>
                      <th>Name</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clients.map((client) => (
                      <tr key={client.client_id}>
                        <td>
                          <code className="bg-light px-2 py-1 rounded">{client.client_id}</code>
                        </td>
                        <td>
                          <strong>{client.name}</strong>
                        </td>
                        <td>
                          <Badge bg={client.is_active ? 'success' : 'secondary'}>
                            {client.is_active ? '✅ Active' : '❌ Inactive'}
                          </Badge>
                        </td>
                        <td>
                          {client.created_at ? new Date(client.created_at).toLocaleDateString() : 'N/A'}
                        </td>
                        <td>
                          <div className="btn-group" role="group">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => generateToken(client.client_id, client.client_secret)}
                              title="Generate Token"
                            >
                              🎫 Token
                            </Button>
                            <Button
                              variant="outline-info"
                              size="sm"
                              onClick={() => copyToClipboard(client.client_id)}
                              title="Copy Client ID"
                            >
                              📋 ID
                            </Button>
                            <Button
                              variant="outline-warning"
                              size="sm"
                              onClick={() => copyToClipboard(client.client_secret)}
                              title="Copy Client Secret"
                            >
                              🔐 Secret
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => {
                                setSelectedClient(client);
                                setShowDeleteModal(true);
                              }}
                              title="Delete Client"
                            >
                              🗑️ Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>

          {/* Configuration Guide */}
          <Card className="mt-4 shadow-sm">
            <Card.Header className="bg-info text-white">
              <h5 className="mb-0">📖 Integration Configuration Guide</h5>
            </Card.Header>
            <Card.Body>
              <div className="row">
                <div className="col-md-6">
                  <h6>🔧 For VAPI Integration:</h6>
                  <ol>
                    <li>Create a new OAuth2 client using the button above</li>
                    <li>Use the generated Client ID and Client Secret in your VAPI configuration</li>
                    <li>Set the OAuth2 URL to: <code>https://lms.iconinc.co.uk/api/oauth2/token</code></li>
                    <li>Optional: Set scope to <code>webhook</code></li>
                  </ol>
                  
                  <h6>📡 Supported Events:</h6>
                  <ul>
                    <li><code>call.ended</code> - Creates user enquiries automatically</li>
                  </ul>
                </div>
                
                <div className="col-md-6">
                  <h6>🤖 Target Assistants:</h6>
                  <ul>
                    <li><code>iconincBookingAgent</code></li>
                    <li><code>iconincViewingAgent</code></li>
                  </ul>
                  
                  <h6>⚡ Quick Actions:</h6>
                  <ul>
                    <li>Click <strong>🎫 Token</strong> to generate an access token</li>
                    <li>Click <strong>📋 ID</strong> to copy Client ID</li>
                    <li>Click <strong>🔐 Secret</strong> to copy Client Secret</li>
                  </ul>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Create Client Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Create New OAuth2 Client</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label><strong>Client Name *</strong></Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter client name (e.g., VAPI Webhook Client)"
                value={newClient.name}
                onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                required
              />
              <Form.Text className="text-muted">
                A descriptive name for this OAuth2 client
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Client ID</Form.Label>
              <div className="d-flex gap-2">
                <Form.Control
                  type="text"
                  placeholder="Enter client ID or leave empty to auto-generate"
                  value={newClient.client_id}
                  onChange={(e) => setNewClient({ ...newClient, client_id: e.target.value })}
                />
                <Button variant="outline-secondary" onClick={generateRandomCredentials}>
                  🎲 Generate
                </Button>
              </div>
              <Form.Text className="text-muted">
                Leave empty to auto-generate a secure client ID
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Client Secret</Form.Label>
              <div className="d-flex gap-2">
                <Form.Control
                  type="text"
                  placeholder="Enter client secret or leave empty to auto-generate"
                  value={newClient.client_secret}
                  onChange={(e) => setNewClient({ ...newClient, client_secret: e.target.value })}
                />
                <Button variant="outline-secondary" onClick={generateRandomCredentials}>
                  🎲 Generate
                </Button>
              </div>
              <Form.Text className="text-muted">
                Leave empty to auto-generate a secure client secret
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleCreateClient}>
            Create Client
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete OAuth2 Client</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete the OAuth2 client <strong>{selectedClient?.name}</strong>?</p>
          <p className="text-danger">This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteClient}>
            Delete Client
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Integrations; 